import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Ordered list of CSS selectors to tween between. */
  path: string[];
  /** Total loop duration across all segments. */
  durationMs: number;
  /** If false the animation plays once and stays at the last point. */
  loop: boolean;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Resolve a selector to a rect. Supports a custom `::eq(N)` pseudo at the end
 * of any selector — e.g. `[data-guide="block"]::eq(1)` picks the second
 * matching element. This sidesteps `:nth-of-type()` which counts all
 * siblings regardless of filter (broken for our use case since the canvas
 * contains many siblings of different guide types).
 */
function resolveRect(selector: string): Rect | null {
  let sel = selector;
  let index = 0;
  const m = /::eq\((\d+)\)\s*$/.exec(selector);
  if (m) {
    index = parseInt(m[1], 10);
    sel = selector.slice(0, m.index);
  }
  const matches = document.querySelectorAll<HTMLElement>(sel);
  const el = matches[index];
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

function lerpRect(a: Rect, b: Rect, t: number): Rect {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    w: a.w + (b.w - a.w) * t,
    h: a.h + (b.h - a.h) * t,
  };
}

// easeInOutQuart — pauses noticeably at each target for readability.
function ease(t: number): number {
  return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
}

/**
 * Decorative spotlight that smoothly moves between a sequence of targets to
 * demonstrate multi-step interactions (e.g. drag a connector from port A to
 * port B). Renders on top of GuideOverlay's static highlight without blocking
 * pointer events. Auto-resolves targets from the DOM each frame so it keeps up
 * with layout changes.
 */
export function AnimatedHighlight({ path, durationMs, loop }: Props) {
  const [rect, setRect] = useState<Rect | null>(null);
  const rafRef = useRef<number | null>(null);
  const startTsRef = useRef<number | null>(null);

  useEffect(() => {
    if (path.length === 0) return;
    const segmentMs = durationMs / Math.max(1, path.length - 1 || 1);

    const tick = (ts: number) => {
      if (startTsRef.current == null) startTsRef.current = ts;
      const elapsed = ts - startTsRef.current;
      const totalSegments = Math.max(1, path.length - 1);
      const totalMs = segmentMs * totalSegments;

      let progress = elapsed / totalMs;
      if (progress >= 1) {
        if (loop) {
          startTsRef.current = ts;
          progress = 0;
        } else {
          progress = 1;
        }
      }

      const globalT = progress * totalSegments;
      const segIndex = Math.min(totalSegments - 1, Math.floor(globalT));
      const localT = ease(globalT - segIndex);
      const a = resolveRect(path[segIndex]);
      const b = resolveRect(path[Math.min(path.length - 1, segIndex + 1)]);
      if (a && b) setRect(lerpRect(a, b, localT));
      else if (a) setRect(a);

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      startTsRef.current = null;
    };
  }, [path, durationMs, loop]);

  if (!rect) return null;

  const pad = 4;
  return (
    <div
      aria-hidden="true"
      className="absolute pointer-events-none"
      style={{
        top: rect.y - pad,
        left: rect.x - pad,
        width: Math.max(24, rect.w + pad * 2),
        height: Math.max(24, rect.h + pad * 2),
        borderRadius: 12,
        border: '2px dashed hsl(var(--primary))',
        boxShadow: '0 0 18px 2px hsl(var(--primary) / 0.6)',
        background: 'hsl(var(--primary) / 0.08)',
      }}
    />
  );
}
